import { useEffect, useState, useRef } from "react";
import "./AppPayConfiguration.css";
import { Building2, Calendar, MapPin, Shield, FileText, Landmark, User } from "lucide-react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectUIStates, selectUser, setUIStates } from "../../../redux/features/appData";
import { Company, CheckCompany, Workspace, CheckOnboardingIssue, isCheckError, CheckResponseError, CheckEnrollmentProfile } from "winslow-core";
import { apiGetCompanyByWorkspaceId } from "../../../services/CompanyService";
import { 
    apiCheckGetCompany,
    apiCheckGetCompanyFederalEIN,
    apiCheckGetSignatories,
    apiCheckGetCompanyOnboardLink,
    apiCheckStartImplementation,
    apiCheckGetEnrollmentProfile,
    apiCheckGetActiveEmployeesAndContractorsCount
} from "../../../services/PayrollService";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../../config";
import { Alert, AlertDescription, Button } from "../../../ds2/components";
import { ModalComponentWrapper } from "../../shared/ModalComponentWrapper";
import Modal from "../../shared/Modal";
import { AppPayCompanyOnboardingStatus } from "./AppPayCompanyOnboardingStatus";
import { CheckEmbeddedComponentV2 } from "../../payroll/CheckEmbeddedComponentV2";
import { AppPayWorkplaces } from "./AppPayWorkplaces";
import { AppPayPaySchedules } from "./AppPayPaySchedules";
import { AppPayCompanyConfig } from "./AppPayCompanyConfig";
import { AppPayBankAccounts } from "./AppPayBankAccounts";
import { AppPayFilings } from "./AppPayFilings";
import { AppPaySignatories } from "./AppPaySignatories";
import { PAY_CONFIGURATION_TABS, TAB_NUMBERS, TAB_NUMBER_TO_ID } from "./payConfigurationConstants";
import { AppCheckErrorMessage } from "./AppCheckErrorMessage";
import { AppCheckCompanyStatus } from "./AppCheckCompanyStatus";

// Re-export for backward compatibility
export { PAY_CONFIGURATION_TABS };

type AppPayConfigurationProps = {
};

export function AppPayConfiguration({}: AppPayConfigurationProps) {
    const uiStates = useSelector(selectUIStates);
    const storeUser = useSelector(selectUser);
    const dispatch = useDispatch();
    const params = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    
    const workspace = storeUser?.workspaces.find(
        (w: Workspace) => w.id === storeUser.currentWorkspaceId
    );
    
    const [tab, setTab] = useState<string>(PAY_CONFIGURATION_TABS.COMPANY);
    const [company, setCompany] = useState<Company | null>(null);
    const [checkCompany, setCheckCompany] = useState<CheckCompany | null>(null);
    const [federalEIN, setFederalEIN] = useState<string | null>(null);
    const [enrollmentProfile, setEnrollmentProfile] = useState<CheckEnrollmentProfile | null>(null);
    const [activeCheckEmployeesContractorsCount, setActiveCheckEmployeesContractorsCount] = useState<number | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [showOnboardingStatusModal, setShowOnboardingStatusModal] = useState<boolean>(false);
    const [showOnboardingLinkModal, setShowOnboardingLinkModal] = useState<boolean>(false);
    const [onboardingLink, setOnboardingLink] = useState<string | null>(null);
    const [loadingOnboardingLink, setLoadingOnboardingLink] = useState<boolean>(false);

    const [checkResponseError, setCheckResponseError] = useState<CheckResponseError | undefined>(undefined);

    useEffect(() => {
        dispatch(setUIStates({...uiStates, hrisLocation: "Payroll Configuration"}));
    }, []);

    useEffect(() => {
        // Check URL params first (for route-based tabs)
        if (params.tab) {
            setTab(params.tab as string);
            return;
        }
        
        // Check query params (for query string-based tabs with numeric IDs)
        const tabParam = searchParams.get("tab");
        if (tabParam) {
            // Try to parse as number first
            const tabNumber = parseInt(tabParam, 10);
            if (!isNaN(tabNumber) && TAB_NUMBER_TO_ID[tabNumber]) {
                setTab(TAB_NUMBER_TO_ID[tabNumber]);
            } else {
                // Fallback to string-based tab ID
                setTab(tabParam);
            }
        }
    }, [params.tab, searchParams]);

    useEffect(() => {
        if (workspace?.id) {
            loadCompany();
        }
    }, [workspace?.id]);

    // Redirect away from Filings/Tax Settings tabs if implementation is not completed
    useEffect(() => {
        if (checkCompany && checkCompany.implementation?.status !== "completed") {
            if (tab === PAY_CONFIGURATION_TABS.FILINGS || tab === PAY_CONFIGURATION_TABS.TAX_SETTINGS) {
                setTab(PAY_CONFIGURATION_TABS.COMPANY);
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.set("tab", TAB_NUMBERS[PAY_CONFIGURATION_TABS.COMPANY].toString());
                setSearchParams(newSearchParams, { replace: true });
            }
        }
    }, [checkCompany?.implementation?.status, tab, searchParams, setSearchParams]);

    const handleTabChange = (tabId: string) => {
        setTab(tabId);
        const tabNumber = TAB_NUMBERS[tabId];
        if (tabNumber) {
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set("tab", tabNumber.toString());
            setSearchParams(newSearchParams, { replace: true });
        }
    };

    // Get setup parameters issues from onboarding status (for tax settings)
    const getSetupParametersIssues = (): CheckOnboardingIssue[] => {
        if (!checkCompany?.onboard?.setup_parameters) {
            return [];
        }
        return checkCompany.onboard.setup_parameters.filter(
            (issue: CheckOnboardingIssue) => issue.status !== "completed"
        );
    };

    // Get filing authorization issues from onboarding status (for filings tab alert icon)
    const getFilingIssues = (): CheckOnboardingIssue[] => {
        if (!checkCompany?.onboard?.filing_authorization) {
            return [];
        }
        return checkCompany.onboard.filing_authorization.filter(
            (issue: CheckOnboardingIssue) => issue.status !== "completed"
        );
    };

   

    const loadCompany = async () => {
        if (!workspace?.id) return;

        setLoading(true);
        
        const companyData = await apiGetCompanyByWorkspaceId(workspace.id);

        if (!companyData) {
            toast("Error loading company data", toastFail);
            setLoading(false);
            return;
        }

        setCompany(companyData);

        // Load CheckCompany data if checkCompanyId exists
        if (companyData?.checkCompanyId) {
            const apiResponse = await apiCheckGetCompany(companyData.checkCompanyId);

            if (!apiResponse) {
                toast("Error loading extended company data", toastFail);
                setLoading(false);
                return;
            }

            console.log("[AppPayConfiguration] apiResponse: ", apiResponse);
            
            const checkCompanyData = apiResponse.data;

            if (!checkCompanyData) {
                toast("Error loading extended company data", toastFail);
                setLoading(false);
                return;
            }

            setCheckCompany(checkCompanyData);

            const apiResponseFederalEIN = await apiCheckGetCompanyFederalEIN(companyData.checkCompanyId);
            const federalEINResult = apiResponseFederalEIN?.data;
            setCheckResponseError(apiResponseFederalEIN?.checkResponseError);

            if (!apiResponseFederalEIN || isCheckError(apiResponseFederalEIN) || !federalEINResult) {
                toast("Error loading federal EIN", toastFail);
                setLoading(false);
                return;
            }

            setFederalEIN(federalEINResult);

            // get enrollment profile
            if (checkCompanyData?.id) {
                const apiResponseEnrollmentProfile = await apiCheckGetEnrollmentProfile(checkCompanyData.id);
                const enrollmentProfileData = apiResponseEnrollmentProfile?.data;
                setCheckResponseError(apiResponseEnrollmentProfile?.checkResponseError);

                if (!apiResponseEnrollmentProfile || isCheckError(apiResponseEnrollmentProfile) || !enrollmentProfileData) {
                    toast("Error loading enrollment profile", toastFail);
                    setLoading(false);
                    return;
                }

                setEnrollmentProfile(enrollmentProfileData);
            }

            // get active check employees/contractors count from database
            if (workspace?.id) {
                const apiResponseActiveCount = await apiCheckGetActiveEmployeesAndContractorsCount(workspace.id);
                const activeCount = apiResponseActiveCount?.data ?? null;
                setActiveCheckEmployeesContractorsCount(activeCount);
            }
        }

        setLoading(false);
    };

    const handleLaunchCheckOnboarding = async () => {
        if (!checkCompany?.id) {
            toast("Company ID not found", toastFail);
            return;
        }

        setLoadingOnboardingLink(true);
        setShowOnboardingLinkModal(true);
        setCheckResponseError(undefined);

        try {
            const apiResponseSignatories = await apiCheckGetSignatories(checkCompany.id);
            const signatories = apiResponseSignatories?.data;
            setCheckResponseError(apiResponseSignatories?.checkResponseError);

            if (!apiResponseSignatories || isCheckError(apiResponseSignatories) || !signatories) {
                toast("Error loading signatories", toastFail);
                setShowOnboardingLinkModal(false);
                setLoadingOnboardingLink(false);
                return;
            }
        
            if (!signatories || signatories.length === 0) {
                toast("No signatories found. Please create a signatory first.", toastFail);
                setShowOnboardingLinkModal(false);
                setLoadingOnboardingLink(false);
                return;
            }

            const apiResponseOnboardingLink = await apiCheckGetCompanyOnboardLink(checkCompany.id, signatories[0].id);
            const link = apiResponseOnboardingLink?.data;
            setCheckResponseError(apiResponseOnboardingLink?.checkResponseError);

            if (!apiResponseOnboardingLink || isCheckError(apiResponseOnboardingLink) || !link) {
                toast("Error loading onboarding link", toastFail);
                setShowOnboardingLinkModal(false);
                setLoadingOnboardingLink(false);
                return;
            }
            
            if (!link) {
                toast("Failed to generate onboarding link", toastFail);
                setShowOnboardingLinkModal(false);
            } else {
                setOnboardingLink(link);
            }
        } catch (error) {
            console.error("[AppPayConfiguration] Error generating onboarding link:", error);
            toast(`Error generating onboarding link: ${error instanceof Error ? error.message : 'Unknown error'}`, toastFail);
            setShowOnboardingLinkModal(false);
        } finally {
            setLoadingOnboardingLink(false);
        }
    }

    const handleLaunchCheckImplementation = async () => {
        if (!checkCompany?.id) {
            toast("Company ID not found", toastFail);
            return;
        }

        setCheckResponseError(undefined);

        const apiResponse = await apiCheckStartImplementation(checkCompany.id);
        const response = apiResponse?.data;
        setCheckResponseError(apiResponse?.checkResponseError);

        if (!apiResponse || isCheckError(apiResponse) || !response) {
            toast("Error starting implementation", toastFail);
            return;
        }
        
        toast("Implementation started", toastSuccess);
        loadCompany();
    }

    const filingIssues = getFilingIssues();
    const hasFilingIssues = filingIssues.length > 0;
    const setupParametersIssues = getSetupParametersIssues();
    const hasSetupParametersIssues = setupParametersIssues.length > 0;

    console.log("[AppPayConfiguration] checkCompany: ", checkCompany);
    console.log("[AppPayConfiguration] enrollmentProfile: ", enrollmentProfile);
    console.log("[AppPayConfiguration] employee count: ", enrollmentProfile?.employee_count);

    return (
        <div className="app-pay-configuration">
            <div className="app-pay-configuration-tabs">
                <div 
                    className={tab === PAY_CONFIGURATION_TABS.COMPANY ? 
                        "app-pay-configuration-tab-selected" : "app-pay-configuration-tab"
                    }
                    onClick={() => handleTabChange(PAY_CONFIGURATION_TABS.COMPANY)}
                >
                    <Building2 size={16} />
                    <span>Company</span>
                </div>
                <div 
                    className={tab === PAY_CONFIGURATION_TABS.SIGNATORIES ? 
                        "app-pay-configuration-tab-selected" : "app-pay-configuration-tab"
                    }
                    onClick={() => handleTabChange(PAY_CONFIGURATION_TABS.SIGNATORIES)}
                >
                    <User size={16} />
                    <span>Signatories</span>
                </div>
                <div 
                    className={tab === PAY_CONFIGURATION_TABS.BANK_ACCOUNTS ? 
                        "app-pay-configuration-tab-selected" : "app-pay-configuration-tab"
                    }
                    onClick={() => handleTabChange(PAY_CONFIGURATION_TABS.BANK_ACCOUNTS)}
                >
                    <Landmark size={16} />
                    <span>Bank Accounts</span>
                </div>
                <div 
                    className={tab === PAY_CONFIGURATION_TABS.PAY_SCHEDULES ? 
                        "app-pay-configuration-tab-selected" : "app-pay-configuration-tab"
                    }
                    onClick={() => handleTabChange(PAY_CONFIGURATION_TABS.PAY_SCHEDULES)}
                >
                    <Calendar size={16} />
                    <span>Pay Schedules</span>
                </div>
                <div 
                    className={tab === PAY_CONFIGURATION_TABS.WORKPLACES ? 
                        "app-pay-configuration-tab-selected" : "app-pay-configuration-tab"
                    }
                    onClick={() => handleTabChange(PAY_CONFIGURATION_TABS.WORKPLACES)}
                >
                    <MapPin size={16} />
                    <span>Workplaces</span>
                </div>
                {checkCompany?.implementation?.status === "completed" && (
                    <>
                        <div 
                            className={tab === PAY_CONFIGURATION_TABS.FILINGS ? 
                                "app-pay-configuration-tab-selected" : "app-pay-configuration-tab"
                            }
                            onClick={() => handleTabChange(PAY_CONFIGURATION_TABS.FILINGS)}
                        >
                            <FileText size={16} />
                            <span>Filings</span>
                        </div>
                        <div 
                            className={tab === PAY_CONFIGURATION_TABS.TAX_SETTINGS ? 
                                "app-pay-configuration-tab-selected" : "app-pay-configuration-tab"
                            }
                            onClick={() => handleTabChange(PAY_CONFIGURATION_TABS.TAX_SETTINGS)}
                        >
                            <Shield size={16} />
                            <span>Tax Settings</span>
                        </div>
                    </>
                )}
            </div>

            {loading ? (
                <div className="app-pay-configuration-content">
                    <p>Loading company data...</p>
                </div>
            ) : (
                <>
                    {checkCompany && enrollmentProfile && 
                        <AppCheckCompanyStatus 
                            checkCompany={checkCompany}
                            enrollmentProfile={enrollmentProfile}
                            activeCheckEmployeesContractorsCount={activeCheckEmployeesContractorsCount}
                            handleLaunchCheckOnboarding={handleLaunchCheckOnboarding}
                            handleLaunchCheckImplementation={handleLaunchCheckImplementation}
                        />
                    }

                    <AppCheckErrorMessage checkResponseError={checkResponseError} />

                    {PAY_CONFIGURATION_TABS.COMPANY === tab &&
                        <AppPayCompanyConfig 
                            company={company}
                            checkCompany={checkCompany}
                            federalEIN={federalEIN}
                            onCompanyUpdate={loadCompany}
                        />
                    }
                    {PAY_CONFIGURATION_TABS.SIGNATORIES === tab &&
                        <AppPaySignatories 
                            company={company}
                            checkCompany={checkCompany}
                        />
                    }
                    {PAY_CONFIGURATION_TABS.BANK_ACCOUNTS === tab &&
                        <AppPayBankAccounts 
                            company={company}
                            checkCompany={checkCompany}
                        />
                    }
                    {PAY_CONFIGURATION_TABS.FILINGS === tab && checkCompany?.implementation?.status === "completed" &&
                        <AppPayFilings 
                            company={company}
                            checkCompany={checkCompany}
                        />
                    }
                    {PAY_CONFIGURATION_TABS.PAY_SCHEDULES === tab &&
                        <AppPayPaySchedules 
                            company={company}
                            checkCompany={checkCompany}
                        />
                    }
                    {PAY_CONFIGURATION_TABS.WORKPLACES === tab &&
                        <AppPayWorkplaces 
                            company={company}
                            checkCompany={checkCompany}
                        />
                    }
                    {PAY_CONFIGURATION_TABS.TAX_SETTINGS === tab && checkCompany?.implementation?.status === "completed" &&
                        <div className="app-pay-configuration-content">
                            <h2>Tax Settings</h2>
                            <p>Tax configuration will be displayed here.</p>
                        </div>
                    }
                </>
            )}

            {checkCompany && (
                <ModalComponentWrapper
                    isOpen={showOnboardingStatusModal}
                    onClose={() => setShowOnboardingStatusModal(false)}
                    style={{ width: "90%", maxWidth: "900px", maxHeight: "90vh", overflow: "auto" }}
                >
                    <AppPayCompanyOnboardingStatus 
                        checkCompany={checkCompany}
                        onResolve={() => setShowOnboardingStatusModal(false)}
                    />
                </ModalComponentWrapper>
            )}

            <Modal
                isOpen={showOnboardingLinkModal}
                onClose={() => {
                    setShowOnboardingLinkModal(false);
                    setOnboardingLink(null);
                }}
                style={{ 
                    width: "90%",
                    maxWidth: "1200px",
                    maxHeight: "95vh",
                    // overflow: "auto" 
                }}
                escapeToClose={true}
                allowClickout={true}
            >
                <div className="app-pay-config-onboarding-content">
                    {/* <h2 className="app-pay-config-onboarding-title">
                        Complete Onboarding
                    </h2> */}
                    {loadingOnboardingLink ? (
                        <div className="app-pay-config-onboarding-loading">
                            <p>Loading onboarding form...</p>
                        </div>
                    ) : onboardingLink ? (
                        <div className="app-pay-config-onboarding-form">
                            <CheckEmbeddedComponentV2
                                url={onboardingLink}
                                title="Company Onboarding"
                                onEvent={(event, data) => {
                                    console.log("[AppPayConfiguration] Onboarding event:", event, data);
                                    // Reload company data when onboarding is completed
                                    if (event === "onboarding.completed" || event === "onboarding.submitted") {
                                        loadCompany();
                                        setShowOnboardingLinkModal(false);
                                        setOnboardingLink(null);
                                    }
                                }}
                            />
                        </div>
                    ) : (
                        <div className="app-pay-config-onboarding-error">
                            <p>Failed to load onboarding form. Please try again.</p>
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
}